import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditWorkExperienceStyle } from './EditWorkExperience.styles';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import { Divider, Typography, debounce } from '@mui/material';
import { TypeAhead } from '@AMIEWEB/Common';
import { brandsSelector, disciplineDataSelector, specialtiesSelector } from 'store/redux-store/lookup/lookup.selectors';
import { useSelector } from 'react-redux';
import { IEditWorkExperience } from '../Models/IEditWorkExperience';
import { ff_ui_candidate_manage_EquipmentProcedure } from 'app/constants/FeatureFlags/Candidate/Keys';
import { useDecision } from '@optimizely/react-sdk';
import FormInputTextField from './FormInputTextField';
import { IOption } from './FormInputDropDown';
import { FormInputRadio } from './FormInputRadio';
import { CustomTooltip } from '@AMIEWEB/Common';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FormInputDatePicker } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDatePicker';
import { FormInputCheckbox } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputCheckbox';
import { getCitiesAndZipsByStates } from 'app/services/SharedServices/SharedServices';
import statesData from 'app/assets/jsons/State.json';
import { FormInputDropdown } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDropdown';
import { ITypeAheadOption } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PreferencesTab/CustomComponents/ControlledTypeAhead';

export interface EditWorkExperienceProps extends IEditWorkExperience {}

enum CountryCodes {
  USA = 1,
  CANADA = 2,
}

export const EditWorkExperience = props => {
  const { formState: { errors }, setValue, getValues, watch, control, formState, clearErrors, setError } = useFormContext();
  const { shouldDisableEdit } = props;
  const { classes } = useEditWorkExperienceStyle();
  const { t } = useTranslation();
  const [candidateManageEquipmentProcedure] = useDecision(ff_ui_candidate_manage_EquipmentProcedure);
  const [cityOptions, setCityOptions] = useState<IOption[]>([]);
  const [statesOptions, setStatesOptions] = useState<IOption[]>([]);

  const travelCompanies = useSelector(brandsSelector);
  const disciplines = useSelector(disciplineDataSelector);
  const specialtiesLookup = useSelector(specialtiesSelector);
  const selectedDiscipline = watch('discipline');
  const selectedCountry = watch('country');
  const selectedState = watch('state');
  const selectedCity = watch('city');
  const isSupervisor = useWatch<boolean>({ name: 'isSupervisor' });
  const isTeleTherapy = useWatch<boolean>({ name: 'isTeleTherapy' });
  const isTeachingHospital = useWatch<boolean>({ name: 'isTeachingHospital' });
  const isLocalStaffingAgency = useWatch<boolean>({ name: 'isLocalStaffingAgency' });
  const selectedStartValue = useWatch<Date>({ name: 'currentlyWorkingStartDate' });
  const selectedEndValue = useWatch<Date>({ name: 'currentlyWorkingEndDate' });

  const [filteredSpecialty, setFilteredSpecialty] = useState<ITypeAheadOption[]>(
    specialtiesLookup.filter(data => {
      return data.object.DisciplineID === getValues('discipline')?.id;
    }),
  );

  const [disciplineOptions, setDisciplineOptions] = useState(
    disciplines.map(e => {
      return { id: e.object.ID, label: e.label, value: e.object.ID };
    }),
  );

  const travelerCompaniesOption: IOption[] = travelCompanies?.map(x => {
    return {
      id: x.value,
      label: x.name,
      value: x.name,
    };
  });

  useEffect(() => {
    const filteredData = specialtiesLookup.filter(data => {
      return data.object.DisciplineID === getValues('discipline')?.id;
    });

    // check if the specialty is the first render
    setFilteredSpecialty(filteredData);

    if (filteredData.length) {
      const selectedSpecialty = getValues('specialty');
      const filteredSpecialties = filteredData.filter(e => e.object.SpecialtyID == selectedSpecialty);
      if (filteredSpecialties.length) {
        setValue('specialty', filteredSpecialties[0]?.label);
      } else {
        setValue('specialty', filteredData[0]?.label, { shouldDirty: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDiscipline]);

  useEffect(() => {
    const endFromDate = selectedEndValue ? new Date(selectedEndValue) : null;
    const startDate = selectedStartValue ? new Date(selectedStartValue) : null;

    const currentlyWorking = getValues('currentlyWorkingIsCurrentlyWorking');
    if (!currentlyWorking && endFromDate?.getTime() < startDate?.getTime()) {
      setError('currentlyWorkingEndDate', {
        type: 'validate',
        message: 'End date must be greater than start date',
      });
    } else {
      clearErrors('currentlyWorkingEndDate');
    }

    if (startDate?.getTime() > endFromDate?.getTime()) {
      setError('currentlyWorkingStartDate', {
        type: 'validate',
        message: 'Start date must be less than end date',
      });
    } else {
      clearErrors('currentlyWorkingStartDate');
    }
  }, [selectedStartValue, selectedEndValue]);

  const handleCurrentlyWorkingChange = event => {
    if (event.target.value === 'true') {
      clearErrors('currentlyWorkingReasonForLeaving');
    }
  };

  const handleStartDateChange = event => {
    setValue('currentlyWorkingStartDate', event, { shouldDirty: true });
    clearErrors('currentlyWorkingStartDate');
  };

  const handleCurrentlyWorkingEndDate = event => {
    if (event && event !== '') {
      const currentDate = new Date().getTime();
      const endDate = new Date(event).getTime();
      const isCurrentlyWorking = currentDate < endDate;

      setValue('currentlyWorkingEndDate', event, { shouldDirty: true });

      if (isCurrentlyWorking) {
        clearErrors('currentlyWorkingReasonForLeaving');
      }
      setValue('currentlyWorkingIsCurrentlyWorking', isCurrentlyWorking ? 'true' : 'false', { shouldDirty: true });
    }
  };

  useEffect(() => {
    if (selectedCountry?.id === CountryCodes.USA || selectedCountry?.id === CountryCodes.CANADA) {
      const states = statesData.states
        .filter(e => e.CountryID === selectedCountry?.id)
        .map(e => ({ id: e.ID, label: e.Description, value: e.StateNumber }));

      setStatesOptions(states);
      let optedState = selectedState;
      if (formState['dirtyFields']['country'] || formState['touchedFields']['country']) {
        optedState = states[0];
      }
      setValue('state', optedState);
    } else if (formState['dirtyFields']['country'] || formState['touchedFields']['country']) {
      setStatesOptions([]);
      setValue('state', '');
    }
  }, [selectedCountry]);

  useEffect(() => {
    let city = selectedCity;
    if (selectedCountry?.id === CountryCodes.USA && selectedState) {
      fetchCities();
    } else {
      setCityOptions([]);
    }

    if (
      formState['dirtyFields']['country'] ||
      formState['touchedFields']['country'] ||
      formState['dirtyFields']['state'] ||
      formState['touchedFields']['state']
    ) {
      city = '';
    }

    setTimeout(() => {
      setValue('city', city);
    }, 0);
  }, [selectedState]);

  const fetchCities = async () => {
    const selectedStates = typeof selectedState ? [selectedState?.label] : [];
    const isCitySearch =
      !(formState['dirtyFields']['state'] || formState['touchedFields']['state']) && typeof selectedCity === 'string';

    const result = isCitySearch
      ? getCitiesAndZipsByStates({
          states: selectedStates,
          cities: [],
          searchMode: 'c',
          searchValue: selectedCity,
        })
      : getCitiesAndZipsByStates({
          states: selectedStates,
          cities: [],
        });

    let filteredCities = [];
    result.then(res => {
      filteredCities = res[0]?.cities?.map(item => {
        return { id: item.id, label: item.name };
      });

      if (!(formState['dirtyFields']['state'] || formState['touchedFields']['state']) && typeof selectedCity === 'string') {
        const city = filteredCities.find(x => x.label == selectedCity);
        setValue('city', city);
      }
      setCityOptions(filteredCities);
    });
  };

  const onTextSearch = async (key: string, searchMode: 'c' | 'z') => {
    const selectedStates = selectedState ? [selectedState?.label] : [];
    if (searchMode === 'c') {
      const result = getCitiesAndZipsByStates({
        states: selectedStates,
        cities: [],
        searchMode: searchMode,
        searchValue: key,
      });

      let filteredCities = [];
      result.then(res => {
        filteredCities = res[0]?.cities?.map(item => {
          return { id: item.id, label: item.name };
        });

        setCityOptions(filteredCities);
      });
    }
  };

  const handleSpecialtyChange = event => {
    setValue('specialty', event.target.value, { shouldDirty: true });
  };

  return (
    <>
      <LayoutGrid
        container
        direction="column"
        className={classes.root}
        spacing={4}
        style={{ marginTop: '10px', width: '870px', paddingRight: '24px' }}
      >
        <>
          <LayoutGridItem item>
            <LayoutGrid
              container
              classes={{ root: classes.root }}
              direction="row"
              data-testid="container"
              justifyContent="flex-start"
              spacing={2}
            >
              <LayoutGridItem item xs={5}>
                <FormInputTextField
                  name="facilityName"
                  label={'Facility/Employer *'}
                  defaultValue={getValues('facilityName')}
                />
              </LayoutGridItem>
              <LayoutGridItem item xs={4}>
                <Controller
                  name="discipline"
                  defaultValue={null}
                  control={control}
                  render={({ field }) => (
                    <TypeAhead
                      options={disciplineOptions || []}
                      label="Discipline*"
                      {...field}
                      isError={!!errors['discipline']}
                      isDisabled={shouldDisableEdit}
                    />
                  )}
                />
              </LayoutGridItem>
              <LayoutGridItem item xs={3}>
                <FormInputDropdown
                  standard
                  name={'specialty'}
                  label={`Specialty *`}
                  options={filteredSpecialty.length ? filteredSpecialty : []}
                  handleChange={handleSpecialtyChange}
                  inputValue={getValues('specialty') || ''}
                  isCheckBox={true}
                  disabled={shouldDisableEdit}
                  useMenuItem
                />
              </LayoutGridItem>
            </LayoutGrid>
          </LayoutGridItem>
          <LayoutGridItem item>
            <LayoutGrid
              container
              classes={{ root: classes.root }}
              direction="row"
              data-testid="container"
              justifyContent="flex-start"
              spacing={2}
            >
              <LayoutGridItem item xs={5}>
                <Controller
                  name="country"
                  defaultValue={null}
                  control={control}
                  render={({ field }) => (
                    <TypeAhead options={props.countriesData} label="Country" {...field} isError={!!errors['country']} />
                  )}
                />
              </LayoutGridItem>
            </LayoutGrid>
          </LayoutGridItem>
          <LayoutGridItem item>
            <LayoutGrid
              container
              classes={{ root: classes.root }}
              direction="row"
              data-testid="container"
              justifyContent="flex-start"
              spacing={2}
            >
              <LayoutGridItem item xs={5}>
                {selectedCountry?.id === CountryCodes.USA || selectedCountry?.id === CountryCodes.CANADA ? (
                  <Controller
                    name="state"
                    defaultValue={statesOptions[0] || null}
                    control={control}
                    render={({ field }) => (
                      <TypeAhead options={statesOptions || []} label="State *" {...field} isError={!!errors['state']} />
                    )}
                  />
                ) : (
                  <FormInputTextField name="state" label={'State'} />
                )}
              </LayoutGridItem>
              <LayoutGridItem item xs={4}>
                {selectedCountry?.id === CountryCodes.USA ? (
                  <Controller
                    name="city"
                    defaultValue={null}
                    control={control}
                    render={({ field }) => (
                      <TypeAhead
                        options={cityOptions || []}
                        label="City *"
                        onInputChange={debounce(text => onTextSearch(text, 'c'), 1500)}
                        {...field}
                      />
                    )}
                  />
                ) : (
                  <FormInputTextField name="city" label={'City'} required={false} />
                )}
              </LayoutGridItem>
              <LayoutGridItem item xs={3}>
                <FormInputTextField name="zipCode" label={'Zip Code'} required={false} />
              </LayoutGridItem>
            </LayoutGrid>
          </LayoutGridItem>
          <LayoutGridItem item>
            <Divider></Divider>
          </LayoutGridItem>
          <LayoutGridItem item>
            <LayoutGrid
              container
              classes={{ root: classes.root }}
              direction="column"
              data-testid="container"
              justifyContent="flex-start"
              spacing={2}
            >
              <LayoutGridItem className={`${classes.alingmentField} ${classes.currentlyWorking}`}>
                <Typography component="span">{`Currently Working`}</Typography>
              </LayoutGridItem>
              <LayoutGridItem>
                <LayoutGrid
                  container
                  classes={{ root: classes.root }}
                  direction="row"
                  data-testid="container"
                  justifyContent="flex-start"
                  spacing={3}
                >
                  <LayoutGridItem>
                    <FormInputRadio
                      name="currentlyWorkingIsCurrentlyWorking"
                      handleChange={e => handleCurrentlyWorkingChange(e)}
                    />
                  </LayoutGridItem>
                  <LayoutGridItem className={classes.infoIconAlignment}>
                    <CustomTooltip tooltipContent={t('candidate.workExperienceCard.currentlyWorkingTooltip')}>
                      <ErrorOutlineIcon className={classes.infoIcon} />
                    </CustomTooltip>
                  </LayoutGridItem>
                </LayoutGrid>
              </LayoutGridItem>
            </LayoutGrid>
            <LayoutGrid
              container
              classes={{ root: classes.root }}
              direction="row"
              data-testid="container"
              justifyContent="flex-start"
              spacing={2}
            >
              <LayoutGridItem item xs={4}>
                <FormInputDatePicker
                  name={'currentlyWorkingStartDate'}
                  label={'Start Date*'}
                  handleChange={e => handleStartDateChange(e)}
                  customClass={classes.datePickerInput}
                  labelClass={classes.label}
                  trailingIconVisible={true}
                  inputValue={getValues('currentlyWorkingStartDate')}
                  message={errors['currentlyWorkingStartDate']}
                ></FormInputDatePicker>
              </LayoutGridItem>
              <LayoutGridItem item xs={4}>
                <FormInputDatePicker
                  name="currentlyWorkingEndDate"
                  label={'End Date'}
                  handleChange={e => handleCurrentlyWorkingEndDate(e)}
                  customClass={classes.datePickerInput}
                  labelClass={classes.label}
                  trailingIconVisible={true}
                  inputValue={getValues('currentlyWorkingEndDate')}
                  message={errors['currentlyWorkingEndDate']}
                ></FormInputDatePicker>
              </LayoutGridItem>
              <LayoutGridItem item xs={4}>
                <FormInputTextField
                  name="currentlyWorkingReasonForLeaving"
                  required={false}
                  label={'Reason for Leaving'}
                />
              </LayoutGridItem>
            </LayoutGrid>
          </LayoutGridItem>
          <LayoutGridItem item>
            <Divider></Divider>
          </LayoutGridItem>
          <LayoutGridItem item>
            <LayoutGrid
              container
              classes={{ root: classes.root }}
              direction="row"
              data-testid="container"
              justifyContent="flex-start"
              spacing={2}
            >
              <LayoutGridItem item xs={4}>
                <FormInputTextField
                  name="numberOfBedsInFacility"
                  label={'# of Beds in Facility'}
                  required={false}
                  type={'number'}
                />
              </LayoutGridItem>
              <LayoutGridItem item xs={4}>
                <FormInputTextField
                  name="unitFloorDepartment"
                  label={'Unit/Floor/Dept'}
                  required={false}
                  disabled={shouldDisableEdit}
                />
              </LayoutGridItem>
              <LayoutGridItem item xs={4}>
                <FormInputTextField name="positionHeld" label={'Position Held'} required={false} />
              </LayoutGridItem>
            </LayoutGrid>
          </LayoutGridItem>
          <LayoutGridItem item>
            <Divider></Divider>
          </LayoutGridItem>
          <LayoutGridItem item className={classes.checkBoxContainer}>
            <LayoutGrid
              container
              classes={{ root: classes.root }}
              direction="row"
              data-testid="container"
              justifyContent="flex-start"
              spacing={2}
            >
              <LayoutGridItem item xs={6}>
                <Controller
                  name="travelCompany"
                  defaultValue={null}
                  control={control}
                  render={({ field }) => (
                    <TypeAhead
                      options={travelerCompaniesOption || []}
                      label="Travel Company"
                      {...field}
                      isError={!!errors['travelCompany']}
                    />
                  )}
                />
              </LayoutGridItem>
              <LayoutGridItem item xs={3}>
                <LayoutGrid
                  container
                  classes={{ root: classes.root }}
                  direction="column"
                  data-testid="container"
                  justifyContent="flex-start"
                  spacing={2}
                >
                  <LayoutGridItem item className={classes.checkBoxInput}>
                    <FormInputCheckbox
                      name={`isSupervisor`}
                      isChecked={isSupervisor}
                      checkboxLabel={`Supervisor`}
                      isDisabled={false}
                      checkboxErrorName={`CheckboxError Supervisor`}
                    ></FormInputCheckbox>
                  </LayoutGridItem>
                  <LayoutGridItem item className={classes.checkBoxInput}>
                    <FormInputCheckbox
                      name="isTeachingHospital"
                      label="Teaching Hospital"
                      isChecked={isTeachingHospital}
                      checkboxLabel={`Teaching Hospital`}
                      isDisabled={false}
                      checkboxErrorName={`CheckboxError Teaching Hospital`}
                    />
                  </LayoutGridItem>
                </LayoutGrid>
              </LayoutGridItem>
              <LayoutGridItem item xs={3}>
                <LayoutGrid
                  container
                  classes={{ root: classes.root }}
                  direction="row"
                  data-testid="container"
                  justifyContent="flex-start"
                  spacing={2}
                >
                  <LayoutGridItem item className={classes.checkBoxInput}>
                    <FormInputCheckbox
                      name="isTeleTherapy"
                      label="Teletherapy"
                      isChecked={isTeleTherapy}
                      checkboxLabel={`Teletherapy`}
                      isDisabled={false}
                      checkboxErrorName={`CheckboxError Teletherapy`}
                    />
                  </LayoutGridItem>
                  <LayoutGridItem item className={classes.checkBoxInput}>
                    <FormInputCheckbox
                      name="isLocalStaffingAgency"
                      label="Local Staffing Agency"
                      isChecked={isLocalStaffingAgency}
                      checkboxLabel={`Local Staffing Agency`}
                      isDisabled={false}
                      checkboxErrorName={`CheckboxError Local Staffing Agency`}
                    />
                  </LayoutGridItem>
                </LayoutGrid>
              </LayoutGridItem>
            </LayoutGrid>
          </LayoutGridItem>

          {candidateManageEquipmentProcedure.enabled && (
            <>
              <LayoutGridItem item>
                <Divider></Divider>
              </LayoutGridItem>
              <LayoutGrid item classes={{ root: classes.root }} direction="column" data-testid="container" spacing={2}>
                <LayoutGridItem item xs={12}>
                  <Typography component="span">{t('candidate.referenceGrid.equipmentProcedures')}</Typography>
                </LayoutGridItem>
                <LayoutGridItem item xs={12}>
                  <FormInputTextField
                    name="epnotes"
                    label={t('candidate.referenceGrid.equipmentProcedures')}
                    value={getValues('epnotes')}
                    multiline
                    spellCheck
                    showHelperText={true}
                    characterLimit={1000}
                  />
                </LayoutGridItem>
              </LayoutGrid>
            </>
          )}
        </>
      </LayoutGrid>
    </>
  );
};
